import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import NotAvailable from '../Components/NotAvailable';
import { useNonMmlAdminAccess } from '../utils/oidcClients';
import {
    PARTNER_STATUSES, fullName, listPartnerInquiries, organizationOf, ownerName, statusLabel,
} from '../utils/partnerInquiries';

const PAGE_SIZE = 10;

const STATUS_BADGE = {
    pending: 'badge-primary',
    'in-progress': 'badge-warning',
    resolved: 'badge-success',
    closed: 'badge-secondary',
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

// admin#181 — partner inquiries from /partners (contacts with type "Partner").
function PartnerInquiries() {
    const allowed = useNonMmlAdminAccess();
    const [inquiries, setInquiries] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    // `active` drops a response that lands after the filter or page changed,
    // so the table never shows rows for a previous selection.
    useEffect(() => {
        if (!allowed) return undefined;
        let active = true;
        setLoading(true);
        setLoadError('');
        listPartnerInquiries({ page, limit: PAGE_SIZE, status: statusFilter || undefined })
            .then((result) => {
                if (!active) return;
                setInquiries(result.contacts || []);
                setPagination(result.pagination || { page: 1, pages: 1, total: 0 });
            })
            .catch((err) => {
                if (!active) return;
                const message = err.message || 'Failed to load inquiries.';
                setInquiries([]);
                setLoadError(message);
                toast.error(message);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => { active = false; };
    }, [allowed, page, statusFilter]);

    if (allowed === false) return <NotAvailable />;

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Partner Inquiries</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-sm-3">
                                    <label htmlFor="inquiry-status-filter" className="fwt_600">Status</label>
                                    <select
                                        id="inquiry-status-filter"
                                        className="form-control"
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                    >
                                        <option value="">All</option>
                                        {PARTNER_STATUSES.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table width="100%" className="table table-striped table-bordered txt_lft">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Organization</th>
                                                <th>Email</th>
                                                <th>Submitted</th>
                                                <th>Status</th>
                                                <th>Owner</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadError && (
                                                <tr>
                                                    <td colSpan="7" className="text-center text-danger">
                                                        Couldn&apos;t load inquiries: {loadError}
                                                    </td>
                                                </tr>
                                            )}
                                            {!loadError && inquiries.length === 0 && (
                                                <tr>
                                                    <td colSpan="7" className="text-center text-muted">No inquiries found.</td>
                                                </tr>
                                            )}
                                            {inquiries.map((inquiry) => {
                                                const viewPath = `/partnerInquiriesView/${encodeURIComponent(inquiry._id)}`;
                                                return (
                                                    <tr key={inquiry._id} data-testid={`inquiry-row-${inquiry._id}`}>
                                                        <td><Link to={viewPath}>{fullName(inquiry) || inquiry.email}</Link></td>
                                                        <td>{organizationOf(inquiry) || '—'}</td>
                                                        <td>{inquiry.email}</td>
                                                        <td>{formatDate(inquiry.createdAt)}</td>
                                                        <td>
                                                            <span className={`badge ${STATUS_BADGE[inquiry.status] || 'badge-secondary'}`}>
                                                                {statusLabel(inquiry.status)}
                                                            </span>
                                                        </td>
                                                        <td>{ownerName(inquiry.assignedTo) || <span className="text-muted">Unassigned</span>}</td>
                                                        <td>
                                                            <div align="center">
                                                                <Link to={viewPath} className="crsr_pntr" aria-label={`View inquiry from ${inquiry.email}`}>
                                                                    <i className="fa-solid fa-eye" title="View" />
                                                                </Link>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {pagination.pages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="text-muted f_sz13">
                                                Page {pagination.page} of {pagination.pages} ({pagination.total} inquiries)
                                            </span>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary mr-2"
                                                    disabled={page <= 1}
                                                    onClick={() => setPage((p) => p - 1)}
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={page >= pagination.pages}
                                                    onClick={() => setPage((p) => p + 1)}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default PartnerInquiries;
